import { notification } from 'antd'
import { UploadProps } from 'antd/lib/upload'
import head from 'lodash-es/head'
import { useUploadFileMutation } from '@/api/file-api'
import { useIntl } from '@/i18n/hooks/use-intl'
import { toVoidFunction } from '@/utils/to-void-function'

export type UploadAccept = string[]

export const UPLOAD_ACCEPT_IMAGE = ['image/png', 'image/jpeg']
export const UPLOAD_ACCEPT_ANY = ['*']
const DEFAULT_MAXIMUM_SIZE_IN_BYTE = 5242880

const useUploadProps = (
  accept: UploadAccept = UPLOAD_ACCEPT_IMAGE,
  maximumSize: number = DEFAULT_MAXIMUM_SIZE_IN_BYTE,
): UploadProps => {
  const { formatMessage, formatNumber } = useIntl()

  const [uploadFile] = useUploadFileMutation()

  return {
    listType: 'picture-card',
    accept: accept.join(','),
    customRequest: toVoidFunction(
      async ({ file, onSuccess, onProgress, onError }): Promise<void> => {
        const imageFormData = new FormData()
        imageFormData.append('Files', file)

        onProgress?.({ percent: 99 })

        try {
          const responseData = await uploadFile(imageFormData).unwrap()
          const modelFileItem = head(responseData)

          const isValidResponse = modelFileItem?.id && modelFileItem.signedUrl

          if (isValidResponse) {
            onSuccess?.(modelFileItem)
          } else {
            onError?.(new Error('id and signedUrl should not be undefined'))
          }
        } catch (error) {
          onError?.(error)
        }
      },
    ),
    beforeUpload: (file): boolean => {
      if (!accept.includes('*') && !accept.includes(file.type)) {
        notification.error({
          message: formatMessage({
            id: 'general:notify_upload_failed',
          }),
          description: formatMessage({
            id: 'general:error_file_format_invalid',
          }),
          placement: 'bottomRight',
        })

        return false
      }

      if (maximumSize && file.size > maximumSize) {
        notification.error({
          message: formatMessage({
            id: 'general:notify_upload_failed',
          }),
          description: formatMessage(
            {
              id: 'general:upload_image_limit_size',
            },
            {
              size: formatNumber(maximumSize / 1024 / 1024, {
                isCompactDisplay: true,
                style: 'unit',
                unit: 'megabyte',
              }),
            },
          ),
          placement: 'bottomRight',
        })
        return false
      }

      return true
    },
  }
}

export default useUploadProps
